import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronDown, SlidersHorizontal, X } from "lucide-react";
import { PROPS } from "@/lib/testIds";
import { AMENITY_OPTIONS } from "@/data/properties";
import PropertyCard from "@/components/PropertyCard";
import { useProperties } from "@/hooks/useApiData";

const GUEST_OPTIONS = [1, 2, 3, 4, 5, 6, 8];

const formatLocation = (location) => {
  if (!location) return "";
  if (typeof location === "string") return location;
  if (typeof location === "object") {
    return [location.city, location.state, location.country].filter(Boolean).join(", ");
  }
  return "";
};

export const Properties = () => {
  const [params, setParams] = useSearchParams();
  const [location, setLocation] = useState(params.get("q") || "");
  const [priceMax, setPriceMax] = useState(800);
  const [guests, setGuests] = useState(() => {
    const parsed = Number.parseInt(params.get("guests") || "1", 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  });
  const [guestOpen, setGuestOpen] = useState(false);
  const [date] = useState(params.get("date") || "");
  const [amenities, setAmenities] = useState([]);
  const guestDropdownRef = useRef(null);

  useEffect(() => {
    const next = new URLSearchParams();
    if (location) next.set("q", location);
    if (guests > 1) next.set("guests", String(guests));
    if (date) next.set("date", date);
    setParams(next, { replace: true });
  }, [location, guests, date, setParams]);

  useEffect(() => {
    const closeGuestDropdown = (event) => {
      if (guestDropdownRef.current && !guestDropdownRef.current.contains(event.target)) {
        setGuestOpen(false);
      }
    };
    document.addEventListener("mousedown", closeGuestDropdown);
    return () => document.removeEventListener("mousedown", closeGuestDropdown);
  }, []);

  const { items: allProperties, loading, error } = useProperties({
    price_max: priceMax,
    date: date || undefined,
  });

  const filtered = useMemo(() => {
    const locationNeedle = location.trim().toLowerCase();
    return allProperties.filter((p) => {
      const searchableLocation = `${formatLocation(p.location)} ${p.title || ""} ${p.name || ""}`.toLowerCase();
      if (locationNeedle && !searchableLocation.includes(locationNeedle)) return false;
      const price = Number(p.price_per_night ?? p.price ?? 0);
      const maxGuests = Number(p.max_guests ?? p.guests ?? 0);
      if (price > priceMax) return false;
      if (maxGuests < guests) return false;
      if (amenities.length && !amenities.every((a) => p.amenities?.includes(a))) return false;
      return true;
    });
  }, [allProperties, location, priceMax, guests, amenities]);

  const toggleAmenity = (a) => setAmenities((prev) => prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]);

  const clearAll = () => {
    setLocation("");
    setPriceMax(800);
    setGuests(1);
    setAmenities([]);
    setGuestOpen(false);
  };

  return (
    <main className="overflow-x-hidden pb-20 md:pb-24">
      <section className="mx-auto max-w-[1400px] px-5 pt-8 md:px-10 md:pt-8">
        <h1 className="mt-3 break-words font-display text-5xl font-medium leading-[0.95] tracking-tight md:mt-4 md:text-8xl">Explore Properties</h1>
      </section>

      <div data-testid={PROPS.filterBar} className="sticky top-[64px] z-30 mt-10 max-w-full border-y border-foreground/10 bg-background/85 backdrop-blur-xl md:top-[76px] md:mt-14">
        <div className="mx-auto grid max-w-[1400px] grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-2 px-5 py-3 md:flex md:flex-wrap md:gap-3 md:px-10 md:py-4">
          <div className="flex min-w-0 items-center gap-2 overflow-hidden rounded-full border border-foreground/15 px-3 py-1.5 md:w-auto md:overflow-visible md:px-4 md:py-2">
            <SlidersHorizontal size={14} className="shrink-0 text-muted-foreground" />
            <input data-testid={PROPS.filterLocation} type="text" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Search a place" className="min-w-0 w-full bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none md:w-40" />
          </div>

          <div ref={guestDropdownRef} className="relative shrink-0">
            <button type="button" data-testid={PROPS.filterGuests} aria-expanded={guestOpen} onClick={() => setGuestOpen((open) => !open)} className="inline-flex min-w-[104px] max-w-[116px] items-center justify-between gap-2 rounded-full border border-foreground/15 bg-background px-3 py-1.5 text-sm focus:outline-none md:min-w-[132px] md:max-w-none md:gap-3 md:px-4 md:py-2">
              <span className="truncate">{guests}+ guests</span>
              <ChevronDown size={14} className={`shrink-0 transition-transform ${guestOpen ? "rotate-180" : ""}`} />
            </button>
            {guestOpen && (
              <div className="absolute right-0 top-[calc(100%+8px)] z-50 w-[132px] rounded-2xl border border-foreground/10 bg-background p-1.5 shadow-xl md:left-0 md:right-auto md:w-full md:min-w-[132px]">
                {GUEST_OPTIONS.map((count) => (
                  <button key={count} type="button" onClick={() => { setGuests(count); setGuestOpen(false); }} className={`block w-full rounded-xl px-3 py-2 text-left text-sm transition-colors ${guests === count ? "bg-foreground text-background" : "text-foreground hover:bg-muted"}`}>{count}+ guests</button>
                ))}
              </div>
            )}
          </div>

          <div data-testid={PROPS.filterPrice} className="hidden items-center gap-3 rounded-full border border-foreground/15 px-4 py-2 text-sm md:flex">
            <span className="text-muted-foreground">Max ${priceMax}</span>
            <input type="range" min={150} max={800} step={10} value={priceMax} onChange={(e) => setPriceMax(Number(e.target.value))} className="w-32 accent-[hsl(var(--primary))]" />
          </div>

          <button type="button" onClick={clearAll} aria-label="Clear filters" className="inline-flex shrink-0 items-center justify-center gap-1 text-xs text-muted-foreground hover:text-foreground md:ml-auto"><X size={14} /><span className="hidden sm:inline">Clear</span></button>
        </div>

        <div className="mx-auto flex max-w-[1400px] items-center gap-2 overflow-x-auto overscroll-x-contain px-5 pb-3 no-scrollbar md:flex-wrap md:overflow-visible md:px-10 md:pb-4">
          {AMENITY_OPTIONS.map((a) => {
            const active = amenities.includes(a);
            return <button key={a} data-testid={PROPS.filterAmenity + "-" + a.toLowerCase().replace(/\s/g, "-")} type="button" onClick={() => toggleAmenity(a)} className={`shrink-0 whitespace-nowrap rounded-full border px-3 py-1 text-xs transition-colors duration-300 ${active ? "border-primary bg-primary text-primary-foreground" : "border-foreground/15 text-muted-foreground hover:border-foreground/40 hover:text-foreground"}`}>{a}</button>;
          })}
        </div>
      </div>

      <section className="mx-auto max-w-[1400px] px-5 pt-8 md:px-10 md:pt-10">
        {loading ? (
          <PropertiesSkeleton />
        ) : error ? (
          <div className="border border-dashed border-foreground/20 px-6 py-16 text-center md:py-24">
            <p className="font-display text-3xl md:text-4xl">We couldn’t load properties.</p>
            <p className="mt-3 text-sm text-muted-foreground">Please refresh and try again.</p>
          </div>
        ) : (
          <>
            <p data-testid={PROPS.resultsCount} className="mb-6 text-[10px] uppercase tracking-[0.2em] text-muted-foreground md:mb-8 md:text-xs">Showing {filtered.length} of {allProperties.length}</p>
            <div data-testid={PROPS.grid} className="grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 md:gap-x-8 md:gap-y-14 lg:grid-cols-3">
              {filtered.map((p) => <PropertyCard key={p.id} property={p} />)}
            </div>
            {filtered.length === 0 && (
              <div className="border border-dashed border-foreground/20 px-6 py-16 text-center md:px-8 md:py-24">
                <p className="font-display text-3xl md:text-4xl">Nothing matches.</p>
                <p className="mt-3 text-sm text-muted-foreground md:text-base">Try loosening a filter or two.</p>
                <button type="button" onClick={clearAll} className="mt-6 rounded-full bg-foreground px-6 py-3 text-sm text-background">Reset filters</button>
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
};

const PropertiesSkeleton = () => (
  <div className="animate-pulse">
    <div className="mb-8 h-3 w-40 rounded bg-muted" />
    <div className="grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 md:gap-x-8 md:gap-y-14 lg:grid-cols-3">
      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index}>
          <div className="aspect-[4/3] bg-muted" />
          <div className="mt-4 h-4 w-3/4 rounded bg-muted" />
          <div className="mt-2 h-3 w-1/2 rounded bg-muted" />
          <div className="mt-4 h-4 w-1/3 rounded bg-muted" />
        </div>
      ))}
    </div>
  </div>
);

export default Properties;